<div class="shrink-0 px-6 pt-6">
  <h1 class="headline-small text-primary text-center m-0">{{ title }}</h1>
  @if (!data.viewOnly) {
  <div class="mt-4 flex justify-center" data-testid="recipient-mode-tabs">
    <div class="inline-flex items-center rounded-full bg-surface-container-low p-1" role="tablist">
      <button
        type="button"
        role="tab"
        class="label-large-prominent cursor-pointer rounded-full px-4 py-2"
        [class.bg-surface-bright]="isPrimary"
        [class.text-primary]="isPrimary"
        [class.shadow-sm]="isPrimary"
        [class.text-on-surface-variant]="!isPrimary"
        [attr.aria-selected]="isPrimary"
        (click)="onTabChange(0)">
        {{ 'Primary recipient' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        class="label-large-prominent cursor-pointer rounded-full px-4 py-2"
        [class.bg-surface-bright]="!isPrimary"
        [class.text-primary]="!isPrimary"
        [class.shadow-sm]="!isPrimary"
        [class.text-on-surface-variant]="isPrimary"
        [attr.aria-selected]="!isPrimary"
        (click)="onTabChange(1)">
        {{ 'Cc recipients' | translate }}
      </button>
    </div>
  </div>
  }
</div>

@if (data.viewOnly) {
  <app-dialog-content class="!flex !min-h-0 !flex-col !overflow-hidden">
    <div class="flex min-h-0 flex-auto flex-col gap-2 overflow-y-auto" data-testid="selected-recipient-list">
      @if (selectedPrimary) {
        <ng-container
          *ngTemplateOutlet="selectedRecipientCard; context: { recipient: selectedPrimary, isPrimary: true }">
        </ng-container>
      }
      @for (recipient of selectedCc; track $index) {
        <ng-container
          *ngTemplateOutlet="selectedRecipientCard; context: { recipient: recipient, isPrimary: false }">
        </ng-container>
      }
    </div>
  </app-dialog-content>
} @else {
<app-dialog-content class="!flex !min-h-0 !flex-col !overflow-hidden">
  <div class="flex min-h-0 flex-col gap-4" data-testid="recipient-lookup">
    @if (canEdit) {
      <mat-form-field appearance="outline" class="w-full shrink-0 [&_.mat-mdc-form-field-icon-suffix]:!translate-x-[-10px]" subscriptSizing="dynamic">
        <mat-label>{{ 'Search address book' | translate }}</mat-label>
        <input
          matInput
          id="recipient-search-address-book"
          [formControl]="searchControl"
          [placeholder]="'Search address book by name or address' | translate"
          (input)="onSearchInput()"
          (keydown.enter)="$event.preventDefault(); submitSearch()"
          data-testid="recipient-search-input" />
        <button
          mat-icon-button
          matSuffix
          type="button"
          [attr.aria-label]="'Search' | translate"
          (click)="submitSearch()"
          data-testid="recipient-search-submit">
          <mat-icon>search</mat-icon>
        </button>
      </mat-form-field>
    }

    <div #recipientList class="relative min-h-[320px] flex-auto overflow-y-auto" data-testid="recipient-list">
      @if (isLoading) {
        <div class="flex flex-col gap-2" data-testid="recipient-lookup-skeleton">
          @for (option of skeletonOptions; track option.name) {
            <div
              class="flex items-center gap-3 rounded-lg border border-surface-container-highest bg-surface-bright p-4"
              data-testid="recipient-option-skeleton">
              <div class="flex min-w-0 flex-1 flex-col gap-2">
                <div class="flex flex-col gap-0.5">
                  <app-skeleton [width]="option.name" height="1rem"></app-skeleton>
                  <app-skeleton [width]="option.role" height="0.75rem"></app-skeleton>
                </div>
                <app-skeleton [width]="option.address" height="0.75rem"></app-skeleton>
              </div>
              <app-skeleton width="1.25rem" height="1.25rem" [rounded]="isPrimary ? 'full' : 'sm'"></app-skeleton>
            </div>
          }
        </div>
      } @else if (results.length > 0) {
        @if (isPrimary) {
          <div class="flex flex-col gap-2">
            @for (recipient of results; track $index) {
              <div
                class="flex items-center gap-3 p-4 rounded-lg border border-surface-container-highest bg-surface-bright hover:bg-surface-container-high"
                [class.cursor-pointer]="canEdit"
                [attr.tabindex]="canEdit ? 0 : null"
                data-testid="recipient-lookup-row"
                (click)="selectPrimary($index)"
                (keydown)="onPrimaryKeydown($index, $event)">
                <div class="flex-1 min-w-0 flex flex-col">
                  <app-recipient-details [recipient]="recipient" [truncateName]="true"></app-recipient-details>
                  @if (canUseFreeText && isManualRecipient(recipient)) {
                    <button
                      mat-button
                      type="button"
                      class="!text-primary self-start !min-h-0 !min-w-0 !h-5 !px-0 leading-5 [--mdc-text-button-container-height:20px]"
                      (click)="editManually($index, $event)"
                      data-testid="recipient-edit-manual">
                      <mat-icon svgIcon="edit-pencil"></mat-icon>
                      {{ 'Edit' | translate }}
                    </button>
                  }
                </div>
                <!-- Pointer events belong to the row, so the radio can never race the toggle; keyboard still reports through (change). -->
                <mat-radio-button
                  class="pointer-events-none"
                  name="primary-recipient"
                  [checked]="isPrimarySelected($index)"
                  [disabled]="!canEdit"
                  (change)="onPrimaryRadioChange($index)"></mat-radio-button>
              </div>
            }
          </div>
        } @else {
          <div class="flex flex-col gap-2">
            @for (recipient of results; track $index) {
              <div
                class="flex items-center gap-2 p-4 rounded-lg border border-surface-container-highest bg-surface-bright hover:bg-surface-container-high"
                [class.cursor-pointer]="canEdit"
                [attr.tabindex]="canEdit ? 0 : null"
                data-testid="recipient-lookup-row"
                (click)="toggleCc($index, !isCcSelected($index))"
                (keydown)="onCcKeydown($index, $event)">
                <div class="flex-1 min-w-0 flex flex-col">
                  <app-recipient-details [recipient]="recipient" [truncateName]="true"></app-recipient-details>
                  @if (canUseFreeText && isManualRecipient(recipient)) {
                    <button
                      mat-button
                      type="button"
                      class="!text-primary self-start -ml-1 mt-1 !px-0 [--mdc-text-button-container-height:20px]"
                      (click)="editManually($index, $event)"
                      data-testid="recipient-edit-manual">
                      <mat-icon svgIcon="edit-pencil"></mat-icon>
                      {{ 'Edit' | translate }}
                    </button>
                  }
                </div>
                <!-- Pointer events belong to the row, so the checkbox can never race the toggle; keyboard still reports through (change). -->
                <mat-checkbox
                  class="pointer-events-none"
                  color="primary"
                  [checked]="isCcSelected($index)"
                  [disabled]="!canEdit"
                  (click)="$event.stopPropagation()"
                  (change)="toggleCc($index, $event.checked)"></mat-checkbox>
              </div>
            }
          </div>
        }
      } @else {
        <div class="flex min-h-[320px] flex-col items-center justify-center">
          <p class="title-medium text-primary">{{ 'No recipients found' | translate }}</p>
        </div>
      }
    </div>

    @if (canUseFreeText) {
      <button
        mat-button
        color="primary"
        type="button"
        (click)="addManually()"
        class="shrink-0 self-start"
        data-testid="recipient-add-manually">
        <mat-icon>add</mat-icon>
        {{ 'Add free text recipient' | translate }}
      </button>
    }
  </div>
</app-dialog-content>
}

<ng-template #selectedRecipientCard let-recipient="recipient" let-isPrimary="isPrimary">
  <div
    class="relative flex items-start gap-2 bg-surface-bright border border-surface-container-highest rounded-lg p-4 w-full"
    data-testid="selected-recipient-card">
    <div class="flex-1 min-w-0">
      <app-recipient-details [recipient]="recipient"></app-recipient-details>
    </div>
    <span
      class="shrink-0 self-start label-small text-center text-neutral-variant px-1 py-px rounded-2xl border whitespace-nowrap"
      [class.bg-primary-98]="isPrimary"
      [class.border-primary-80]="isPrimary"
      [class.bg-surface-container-lowest]="!isPrimary"
      [class.border-outline-variant]="!isPrimary"
      [attr.data-testid]="isPrimary ? 'selected-primary-badge' : 'selected-cc-badge'">
      {{ (isPrimary ? 'Primary recipient' : 'CC recipient') | translate }}
    </span>
  </div>
</ng-template>

<div class="shrink-0 border-t border-outline-variant mx-6 mb-3"></div>

<app-dialog-footer class="shrink-0">
  <div class="flex justify-end w-full gap-3">
    <button
      mat-stroked-button
      color="primary"
      type="button"
      (click)="close()"
      class="!px-6 [--mdc-outlined-button-container-height:48px]"
      data-testid="recipient-lookup-cancel">
      {{ 'Cancel' | translate }}
    </button>
    @if (canEdit) {
      <button
        mat-flat-button
        color="primary"
        type="button"
        (click)="save()"
        class="!px-6 [--mdc-filled-button-container-height:48px]"
        data-testid="recipient-lookup-save">
        {{ 'Save' | translate }}
      </button>
    }
  </div>
</app-dialog-footer>
